import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

import { MfaService } from '../../../services/mfa.service';
import { AuthService } from '../../../services/auth.service';
import { MfaStatus } from '../../../models/mfa.model';

@Component({
  selector: 'app-mfa-management',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './mfa-management.component.html',
  styleUrls: ['./mfa-management.component.scss']
})
export class MfaManagementComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  disableForm: FormGroup;
  regenerateForm: FormGroup;
  mfaStatus: MfaStatus = { isEnabled: false, hasBackupCodes: false };

  loading = {
    disable: false,
    regenerate: false
  };

  errors = {
    disable: '',
    regenerate: ''
  };

  showDisableConfirmation = false;
  showRegenerateConfirmation = false;
  newBackupCodes: string[] = [];
  showBackupCodes = false;

  constructor(
    private fb: FormBuilder,
    private mfaService: MfaService,
    private authService: AuthService,
    private router: Router
  ) {
    this.disableForm = this.fb.group({
      verificationCode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      password: ['', [Validators.required]]
    });

    this.regenerateForm = this.fb.group({
      verificationCode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  ngOnInit() {
    this.mfaService.mfaStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.mfaStatus = status;
      });

    // Load current MFA status
    const userInfo = this.authService.getUserInfo();
    if (userInfo.username && userInfo.companyid) {
      this.authService.loadUserMfaStatus(userInfo.username, userInfo.companyid);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDisableMfa() {
    if (this.disableForm.invalid) {
      return;
    }

    const { verificationCode, password } = this.disableForm.value;
    this.loading.disable = true;
    this.errors.disable = '';

    this.mfaService.disable(verificationCode, password)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading.disable = false)
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            // Update MFA status
            this.mfaService.updateMfaStatus({
              isEnabled: false,
              hasBackupCodes: false
            });

            alert('MFA 已成功停用');
            this.disableForm.reset();
            this.showDisableConfirmation = false;
          } else {
            this.errors.disable = response.message || 'MFA 停用失敗';
          }
        },
        error: (error) => {
          console.error('MFA 停用錯誤:', error);
          this.errors.disable = 'MFA 停用過程中發生錯誤，請稍後再試';
        }
      });
  }

  onRegenerateBackupCodes() {
    if (this.regenerateForm.invalid) {
      return;
    }

    const { verificationCode } = this.regenerateForm.value;
    this.loading.regenerate = true;
    this.errors.regenerate = '';

    this.mfaService.regenerateBackupCodes(verificationCode)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading.regenerate = false)
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.backupCodes) {
            this.newBackupCodes = response.backupCodes;
            this.showBackupCodes = true;
            this.regenerateForm.reset();
            this.showRegenerateConfirmation = false;

            // Update MFA status
            this.mfaService.updateMfaStatus({
              ...this.mfaStatus,
              hasBackupCodes: true
            });
          } else {
            this.errors.regenerate = response.message || '備用碼重新生成失敗';
          }
        },
        error: (error) => {
          console.error('備用碼重新生成錯誤:', error);
          this.errors.regenerate = '重新生成備用碼過程中發生錯誤，請稍後再試';
        }
      });
  }

  showDisableDialog() {
    this.showDisableConfirmation = true;
    this.errors.disable = '';
    this.disableForm.reset();
  }

  showRegenerateDialog() {
    this.showRegenerateConfirmation = true;
    this.errors.regenerate = '';
    this.regenerateForm.reset();
  }

  hideDisableDialog() {
    this.showDisableConfirmation = false;
    this.errors.disable = '';
    this.disableForm.reset();
  }

  hideRegenerateDialog() {
    this.showRegenerateConfirmation = false;
    this.errors.regenerate = '';
    this.regenerateForm.reset();
  }

  hideBackupCodes() {
    this.showBackupCodes = false;
    this.newBackupCodes = [];
  }

  navigateToSetup() {
    this.router.navigate(['/mfa-setup']);
  }

  goBack() {
    this.router.navigate(['/lobby']);
  }
}
